import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import { ExternalAudioValidationService } from './external-audio-validation.service';
import { StandardiseTranscriptService } from './standardise-transcript.service';

@Injectable({
  providedIn: 'root',
})
export class ExternalCallDurationValidationService {
  constructor(
    private standardiseTranscriptService: StandardiseTranscriptService,
    private externalAudioValidationService: ExternalAudioValidationService
  ) {}

  validate(transcript: File, audio: File): Observable<ValidationErrors | null> {
    if (!transcript || !audio) {
      return of(null);
    }

    return this.standardiseTranscriptService.standardise(transcript).pipe(
      switchMap((standardised) => from(standardised.text())),
      map((content) => this.preProcessTimestamp(content)),
      switchMap((transcriptContent) => {
        const transcriptEndSeconds = transcriptContent.reduce(
          (max, curr) => (curr.timestampEnd > max ? curr.timestampEnd : max),
          0
        );

        return this.getAudioDurationSeconds(audio).pipe(
          map((audioDurationSeconds) =>
            transcriptEndSeconds > audioDurationSeconds
              ? { durationMismatch: true }
              : null
          )
        );
      }),
      catchError((error) => {
        console.error('Error during duration validation', error);
        return of<ValidationErrors>({ durationValidationError: true });
      })
    );
  }

  private preProcessTimestamp(content: string) {
    const matches = content.matchAll(
      /(\d+)\r\n(\d{2}:\d{2}:\d{2}\.\d{3}) --> (\d{2}:\d{2}:\d{2}\.\d{3})\r\n(.+):(.+)[\n\r]+/g
    );

    const convertTimestampToSeconds = (timestamp: string) => {
      const [hours, minutes, seconds] = timestamp
        .split(':')
        .map((part) => parseFloat(part));

      return hours * 3600 + minutes * 60 + seconds;
    };

    return [...(matches || [])].map(
      ([, index, timestampStart, timestampEnd, speaker, text]) => ({
        index,
        timestampStart: convertTimestampToSeconds(timestampStart),
        timestampEnd: convertTimestampToSeconds(timestampEnd),
        speaker: speaker.trim(),
        content: text.trim(),
      })
    );
  }

  private getAudioDurationSeconds(audio: File) {
    return this.externalAudioValidationService
      .decode(audio)
      .pipe(map((decoded) => decoded.duration));
  }
}
